import { DirectionsBoat, People, ToggleOn } from '@mui/icons-material'
import { Chip, Typography } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useGetVesselClients } from 'src/api/vessel'
import ApprovedEquipmentCustomers from 'src/containers/Equipment/ApprovedEquipmentCustomers'
import { useNavigation } from 'src/context/NavigationContext'
import { IVesselClient } from 'src/generated-types/vessel'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CustomerFeatureFlagsDialog } from 'src/screens/admin/customers/CustomerFeatureFlagsDialog'
import { invalidateCustomer, useGetAdminCustomers } from '../../../api/customer'
import { invalidateCustomerCache, useDeleteCustomer } from '../../../api/customer-admin'
import GenericTable from '../../../components/table/GenericTable'
import UpdateCustomerDialog from '../../../dialogs/customer/UpdateCustomerDialog'
import VesselMappingDialog from '../vessel-mapping/VesselMappingDialog'

export const CustomersAdminTable: React.FC<{
  onRowClick?: (params: any) => void
  getDetailPanelContent?: (params: any) => JSX.Element
}> = ({ onRowClick, getDetailPanelContent }) => {
  const navigate = useNavigation()
  const { t: translate } = useTranslation(['admin-customer', 'common', 'admin-sidebar'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { mutate, isLoading: isDeleting } = useDeleteCustomer()
  const { data: vesselClients = [] } = useGetVesselClients()
  const { data } = useGetAdminCustomers()

  const clientMap = useMemo(() => {
    const map = new Map<number, string>()
    vesselClients.forEach((c: IVesselClient) => map.set(c.clientId, c.name))
    return map
  }, [vesselClients])

  const onDeleteSupplier = (id: EntityId) => {
    showConfirmDialog(
      translate('deleteCustomer', { ns: 'admin-customer' }),
      translate('areYouSureDeleteCustomer', { ns: 'admin-customer' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        mutate(id)
        toast.success(translate('customerDeleted', { ns: 'admin-customer' }))
        pop()
        invalidateCustomerCache.getCustomers()
        invalidateCustomer.getCustomersWithAdminUserPermission()
        invalidateCustomer.getCustomersNameWithProjectPlanningAccess()
      },
    )
  }

  const onUpdate = async id => {
    const added = await showDialog(
      UpdateCustomerDialog,
      {
        componentProps: { id: id as EntityId },
        title: translate('updateCustomer', { ns: 'admin-customer' }),
        opts: { maxWidth: 'md', fullWidth: true },
      },
      undefined,
      false,
    )
    if (!added) return

    invalidateCustomerCache.getCustomers()
    invalidateCustomerCache.getCustomer(id)
    invalidateCustomer.getCustomersNameWithProjectPlanningAccess()
  }

  const handleOpenApprovedCustomersDialog = id => {
    showDialog(
      ApprovedEquipmentCustomers,
      {
        opts: { maxWidth: 'md', fullWidth: true },
        componentProps: {
          customerId: id as EntityId,
        },
      },
      undefined,
      true,
    )
  }

  const onAssignVesselClient = (id: EntityId, currentVesselId: number | undefined) => {
    showDialog(
      VesselMappingDialog,
      {
        componentProps: { customerId: id, currentVesselId, vesselClients },
        title: translate('vessel-mapping', { ns: 'admin-customer' }),
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const handleOpenFeatureFlags = (id: EntityId, name: string) => {
    void showDialog(
      CustomerFeatureFlagsDialog,
      {
        opts: { maxWidth: 'lg', fullWidth: true },
        componentProps: { customerId: id, customerName: name },
      },
      undefined,
      true,
    )
  }

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: translate('name', { ns: 'common' }),
      flex: 1,
      renderCell: params => <span style={{ textTransform: 'capitalize' }}>{params.value}</span>,
    },
    { field: 'organizationNr', headerName: translate('organizationNumber', { ns: 'admin-customer' }), flex: 1 },
    { field: 'address', headerName: translate('address', { ns: 'common' }), flex: 1 },
    { field: 'city', headerName: translate('city', { ns: 'common' }), flex: 1 },
    { field: 'country', headerName: translate('country', { ns: 'common' }), flex: 1 },
    {
      field: 'vesselId',
      headerName: translate('vessel-mapping', { ns: 'admin-customer' }),
      flex: 1,
      renderCell: ({ value }) =>
        value != null ? (
          <Typography variant="body2">{clientMap.get(value) ?? `ID: ${value}`}</Typography>
        ) : (
          <Chip label={translate('not-mapped', { ns: 'admin-customer' })} size="small" />
        ),
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={`features-${id}`}
            label={translate('feature-flags-action', { ns: 'feature-flags' })}
            icon={<ToggleOn fontSize="medium" />}
            onClick={() => handleOpenFeatureFlags(id as EntityId, row.name)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(id as number)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => onDeleteSupplier(id as EntityId)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('approved-customers', { ns: 'main-component' })}
            icon={<People />}
            onClick={() => handleOpenApprovedCustomersDialog(id as EntityId)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={`vessel-${id}`}
            label={translate('vessel-mapping', { ns: 'admin-customer' })}
            icon={<DirectionsBoat />}
            onClick={() => onAssignVesselClient(id as EntityId, row.vesselId)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [isDeleting, clientMap],
  )

  return (
    <GenericTable
      id="customers-table"
      enablePagination
      pageSize={10}
      pageOptions={[10, 25, 50]}
      data={data?.items}
      onRowClick={onRowClick}
      columns={columns}
      enableToolbar={true}
      getRowHeight={() => 'auto'}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
    />
  )
}
